import i18n from "../i18n";
import { siteTexts } from "../data/siteTexts";
import { scheduleItems as rawScheduleItems } from "../data/scheduleData";
import { locations as rawLocations } from "../data/locationsData";

// In-memory reference (same structure as before, loaded from local data)
let cachedSiteTexts = siteTexts;

/**
 * Loads site texts into i18n for the given locale.
 * Now reads from local data instead of Firestore.
 */
export async function loadSiteTexts(locale = "bg") {
  try {
    // Convert flat keys to nested structure for i18n
    const nestedTexts = {};
    Object.keys(cachedSiteTexts).forEach((key) => {
      const value = cachedSiteTexts[key][locale] || cachedSiteTexts[key].bg; // Fallback to BG
      const keys = key.split(".");
      let current = nestedTexts;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
    });

    // Add the dynamic texts to i18n for the given locale
    i18n.addResourceBundle(locale, "translation", nestedTexts, true, true);

    return nestedTexts;
  } catch (error) {
    console.error("Error loading site texts:", error);
    throw error;
  }
}

/**
 * Returns schedule items with localized titles for the given locale.
 * Now reads from local data instead of Firestore.
 */
export async function loadScheduleItems(locale = "bg") {
  return rawScheduleItems.map((item) => {
    let localizedTime = item.time;

    // If time is an object, get the localized version
    if (item.time && typeof item.time === 'object') {
      localizedTime = item.time[locale] || item.time.bg || "";
    }

    return {
      id: item.id,
      time: localizedTime,
      title: item.title[locale] || item.title.bg,
      description: item.description[locale] || item.description.bg,
      locationLink: item.locationLink?.[locale] || item.locationLink?.bg || "",
      locationDetails: item.locationDetails?.[locale] || item.locationDetails?.bg || "",
      locationMapUrl: item.locationMapUrl || "",
      order: item.order,
    };
  });
}

/**
 * Returns locations with localized titles/descriptions for the given locale.
 * Now reads from local data instead of Firestore.
 */
export async function loadLocations(locale = "bg") {
  return rawLocations.map((location) => ({
    id: location.id,
    title: location.title[locale] || location.title.bg,
    description: location.description[locale] || location.description.bg,
    details: location.details?.[locale] || location.details?.bg || "",
    image_url: location.image_url,
    map_url: location.map_url,
    order: location.order,
  }));
}

/**
 * Returns localized texts for a given section prefix.
 * E.g., loadSectionTexts("nav", "en") returns { confirmation: "Confirm" }
 * Same API as before — reads from in-memory data.
 */
export function loadSectionTexts(section, locale = "bg") {
  if (!cachedSiteTexts) return {};

  const result = {};
  const prefix = section + ".";

  Object.keys(cachedSiteTexts).forEach((key) => {
    if (key.startsWith(prefix)) {
      const shortKey = key.slice(prefix.length);
      const value = cachedSiteTexts[key][locale] || cachedSiteTexts[key].bg || "";
      result[shortKey] = value;
    }
  });

  return result;
}

/**
 * Loads all content for a given locale.
 * Now instant since data is local.
 */
export async function loadAllContent(locale = "bg") {
  try {
    await Promise.all([
      loadSiteTexts(locale),
      loadScheduleItems(locale),
      loadLocations(locale),
    ]);
    console.log(`✅ All content loaded for locale: ${locale}`);
  } catch (error) {
    console.error("Error loading all content:", error);
    throw error;
  }
}
